<template>
  <!-- Error boundary catches config validation errors and shows user-friendly message -->
  <ConfigErrorBoundary>
    <FileConverter
      :config="converterConfig"
      @batch-created="handleBatchCreated"
      @job-completed="handleJobCompleted"
      @batch-cleared="handleBatchCleared"
      @error="handleError"
      @credits-updated="handleCreditsUpdated"
    >
      <!-- Override limits slot for PowerPoint-specific display (includes slide count) -->
      <template #dropzone-limits="{ config, concurrencyLabel }">
        <div class="grid grid-cols-1 md:flex md:items-center md:justify-center gap-x-1.5 gap-y-1 text-xs sm:text-sm text-slate-500 text-center">
          <span>{{ config.maxFileSizeFormatted }} or {{ config.maxPageCount }} slides per file</span>
          <span class="hidden md:inline" aria-hidden="true">·</span>
          <span>Up to {{ config.maxBatchCount }} files{{ config.maxBatchInputGb ? ` (${config.maxBatchInputGb} limit)` : '' }}</span>
          <span class="hidden md:inline" aria-hidden="true">·</span>
          <span v-if="concurrencyLabel">{{ concurrencyLabel }}</span>
          <span v-if="concurrencyLabel" class="hidden md:inline" aria-hidden="true">·</span>
          <span class="flex items-center justify-center gap-1">
            <svg class="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500 flex-shrink-0" viewBox="0 0 605 608" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M304.108 0.654297C316.856 0.654702 329.563 1.45818 342.209 3.07422V99.5225C300.652 151.249 253.818 198.237 202.571 239.787C157.781 276.122 109.605 308.359 58.623 335.971H58.5713C58.1443 336.184 57.6565 336.459 57.2295 336.673L57.1787 336.724C55.5113 337.852 54.3828 339.845 54.3828 342.051C54.3831 345.599 57.2909 348.506 60.9004 348.506H254.479C260.407 348.506 265.247 353.295 265.247 359.272V508.439L389.419 352.552C391.463 349.99 394.565 348.495 397.839 348.495H604.157C598.555 386.598 585.744 422.933 566.852 455.709C540.689 501.081 502.742 539.622 455.796 566.746C408.86 593.819 356.475 607.33 304.1 607.30C269.167 607.33 234.184 601.303 200.868 589.249L200.863 589.256C184.228 583.278 168.02 575.744 152.412 566.757C105.467 539.633 67.5197 501.093 41.3574 455.721C15.1952 410.348 0.715874 358.195 0.71582 303.998C0.71582 287.956 2.00729 272.075 4.48828 256.47C10.3654 219.495 22.9531 184.238 41.3672 152.326C67.5295 107.005 105.487 68.3621 152.423 41.2891C199.365 14.218 251.737 0.656145 304.108 0.654297ZM342.219 3.06445C381.62 8.01544 420.279 20.7739 455.806 41.2793C535.747 87.4549 589.577 166.956 603.73 256.461H352.987C347.039 256.461 342.219 251.642 342.219 245.694V3.06445Z" />
            </svg>
            {{ config.costPerUnit }} {{ config.costPerUnit === 1 ? 'credit' : 'credits' }} {{ config.costSuffix }}
          </span>
        </div>
      </template>
    </FileConverter>
  </ConfigErrorBoundary>
</template>

<script setup lang="ts">
import FileConverter from './FileConverter.vue';
import ConfigErrorBoundary from './ConfigErrorBoundary.vue';
import { parseAndValidateConverterConfig } from '../utils/ConfigValidator';

// Parse and validate configuration using fail-fast Zod validation
// This replaces the old manual JSON parsing + lazy fallbacks pattern
// If config is invalid, parseAndValidateConverterConfig() throws with detailed error
// NOTE: jobCategory, labels, and creditCost are now provided by backend
const converterConfig = parseAndValidateConverterConfig('pptx-to-png-converter', 'PowerPoint → PNG');

// Event handlers (optional - for PowerPoint-specific analytics or side effects)
function handleBatchCreated(batchId: string) {
  console.log('[PowerPoint to PNG Converter] Batch created:', batchId);
}

function handleJobCompleted(jobId: string) {
  console.log('[PowerPoint to PNG Converter] Job completed:', jobId);
}

function handleBatchCleared() {
  console.log('[PowerPoint to PNG Converter] Batch cleared');
}

function handleError(error: Error) {
  console.error('[PowerPoint to PNG Converter] Error:', error);
}

function handleCreditsUpdated(credits: number) {
  console.log('[PowerPoint to PNG Converter] Credits updated:', credits);
}
</script>
